import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "./Ticket.css";
import "./menu3.css";
import io from "socket.io-client";
import TicketSidebar from "../components/TicketSidebar";
import { useNavigate } from "react-router-dom";
axios.defaults.withCredentials = true;
const normalizeAttach = (af) => {
  if (!af) return [];
  if (Array.isArray(af)) {
    return af.map((x) => (x ?? "").toString().trim()).filter(Boolean);
  }

  let s = String(af).trim();
  if (!s || s === "[]" || /^null$/i.test(s) || /^undefined$/i.test(s))
    return [];

  try {
    const parsed = JSON.parse(s);
    if (Array.isArray(parsed)) {
      return parsed.map((x) => (x ?? "").toString().trim()).filter(Boolean);
    }
    if (typeof parsed === "string") {
      s = parsed.trim();
    }
  } catch (_) {}

  if (
    s.startsWith("[") &&
    s.endsWith("]") &&
    s.includes("'") &&
    !s.includes('"')
  ) {
    try {
      const fixed = s.replace(/'/g, '"');
      const parsed2 = JSON.parse(fixed);
      if (Array.isArray(parsed2)) {
        return parsed2.map((x) => (x ?? "").toString().trim()).filter(Boolean);
      }
    } catch (_) {}
  }

  s = s.replace(/^\[/, "").replace(/\]$/, "");
  const parts = s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
  return parts
    .map((p) => p.replace(/^"(.*)"$/, "$1").replace(/^'(.*)'$/, "$1"))
    .filter(Boolean);
};

const buildFileUrl = (f) => {
  const val = (f ?? "").toString().trim();
  if (!val) return null;
  if (/^https?:\/\//i.test(val)) return val;
  return `/fileuploads/${encodeURIComponent(val)}`;
};

const Ticket = () => {
  const [tickets, setTickets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [notFoundMessage, setNotFoundMessage] = useState("");
  const [activeMenu] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responses, setResponses] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [socket, setSocket] = useState(null);
  const [isDisabled, setIsDisabled] = useState(false);
  const ITEMS_PER_PAGE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const hasFetched = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    const socketConnection = io("", {});
    setSocket(socketConnection);
    socketConnection.on("connect", () => {});
  }, []);

  useEffect(() => {
    if (!socket) return;
    const onTicketResp = (messageData) => {
      setResponses((prev) => {
        const exists = prev.some((m) => m.id === messageData.id);
        if (exists) return prev;
        const normalized = normalizeAttach(messageData.AttachFiles);
        return [...prev, { ...messageData, AttachFiles: normalized }];
      });
    };
    socket.on("ticketResponse", onTicketResp);
    return () => socket.off("ticketResponse", onTicketResp);
  }, [socket]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size should not exceed 5MB");
        event.target.value = null;
        return;
      }
      setSelectedFile(file);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) return null;
    const formData = new FormData();
    formData.append("file", selectedFile);

    try {
      setIsUploading(true);
      const response = await axios.post(
        "/fileuploads/upload",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        },
      );
      return response.data.filename;
    } catch (error) {
      console.error("Upload error:", error.response || error.message);
      alert("Failed to upload file");
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const fetchResponses = useCallback(async (ticketNumber) => {
    try {
      const response = await axios.get(
        `/ticketadmin/message?ticketNumber=${ticketNumber}`,
        { withCredentials: true },
      );

      const normalized = (response.data || [])
        .map((r) => ({
          ...r,
          AttachFiles: normalizeAttach(r.AttachFiles),
        }))
        .sort((a, b) => a.id - b.id);

      setResponses(normalized);
      setNotFoundMessage("");
    } catch (err) {
      if (err?.response?.status === 401 || err?.response?.status === 403) {
        setResponses([]);
        setNotFoundMessage("ไม่พบข้อมูล");
        return;
      }

      setResponses([]);
      setNotFoundMessage("ไม่พบข้อมูล");
    }
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await fetch(`/ticketcustomer`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (response.status === 401 || response.status === 403) {
        setTickets([]);
        setNotFoundMessage("ไม่พบข้อมูล");
        return;
      }

      if (!response.ok) {
        setTickets([]);
        setNotFoundMessage("ไม่พบข้อมูล");
        return;
      }

      const data = await response.json();

      if (!Array.isArray(data) || data.length === 0) {
        setTickets([]);
        setNotFoundMessage("ไม่พบข้อมูล");
        return;
      }

      setTickets(data);
      setNotFoundMessage("");
    } catch (error) {
      setTickets([]);
      setNotFoundMessage("ไม่พบข้อมูล");
    }
  };

  useEffect(() => {
    if (selectedTicket) {
      fetchResponses(selectedTicket.id);
    }
  }, [selectedTicket, fetchResponses]);

  useEffect(() => {
    if (!hasFetched.current) {
      fetchTickets();
      hasFetched.current = true;
    }
  }, []);

  const formatDate = (input) => {
    if (!input || String(input).trim() === "") return "-";

    try {
      let parsedDate;

      if (input instanceof Date) {
        parsedDate = input;
      } else {
        const text = String(input).trim();
        parsedDate = text.includes("T")
          ? new Date(text)
          : new Date(text.replace(" ", "T"));
      }

      if (isNaN(parsedDate.getTime())) return "-";

      const day = String(parsedDate.getDate()).padStart(2, "0");
      const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
      const year = parsedDate.getFullYear();

      const hour = String(parsedDate.getHours()).padStart(2, "0");
      const minute = String(parsedDate.getMinutes()).padStart(2, "0");

      return `${day}/${month}/${year} ${hour}.${minute} น.`;
    } catch (err) {
      console.error("Date formatting error:", err);
      return "-";
    }
  };

  const handleSendMessage = async () => {
    if ((!newMessage.trim() && !selectedFile) || !selectedTicket) return;

    let fileUrl = null;
    if (selectedFile) fileUrl = await uploadFile();

    const now = new Date(new Date().getTime() + 7 * 60 * 60 * 1000);
    const formattedDate = now.toISOString().slice(0, 19).replace("T", " ");

    const messageData = {
      ticket_id: selectedTicket.id,
      message: newMessage.trim(),
      created_at: formattedDate,
      AttachFiles: fileUrl,
    };

    try {
      socket.emit("sendMessage", messageData);
      setNewMessage("");
      setSelectedFile(null);

      const fileInput = document.getElementById("file-upload");
      if (fileInput) fileInput.value = "";

      fetchResponses(selectedTicket.id);
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Please include a caption for the file or image.");
    }
  };

  const handleUrgentClick = () => {
    if (!selectedTicket) {
      alert("Please select a ticket first.");
      return;
    }
    if (window.confirm("Are you sure you want to send an urgent email?")) {
      setIsDisabled(true);
      sendEmail(selectedTicket)
        .then(() => {
          alert(
            "Urgent notification sent successfully! Button will be locked for 1 hour.",
          );
        })
        .catch((error) => {
          console.error("Error sending urgent email:", error);
          alert("Failed to send urgent notification. Please try again.");
          setIsDisabled(false);
        });
    }
  };

  const sendEmail = async (ticket) => {
    if (!ticket)
      return Promise.reject("Ticket information is required to send an email.");
    const emailData = {
      toEmail: "services@proscope.co.th",
      subject: `Urgent: Ticket #${ticket.id}`,
      message: `Ticket Number: ${ticket.id} ไม่มีการตอบกลับเป็นเวลานาน.`,
    };
    try {
      const response = await axios.post(
        "/ticketcustomer/urgent",
        emailData,
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        },
      );
      return response;
    } catch (error) {
      console.error(
        "Error sending email:",
        error.response?.data || error.message,
      );
      throw error;
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleRowClick = (ticket) => {
    setSelectedTicket(ticket);
    navigate(`/customer/ticket/${ticket.id}`);
  };

  const filteredTickets = tickets.filter((ticket) =>
    String(ticket.id).toLowerCase().includes(searchTerm.toLowerCase()),
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, tickets]);

  const totalPages = Math.ceil(filteredTickets.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedTickets = filteredTickets.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  const getTicketStatusClass = (status) => {
    const value = String(status || "")
      .toLowerCase()
      .trim();

    if (value === "open") return "cx-ticket-state-badge cx-ticket-state-open";
    if (value === "foundcause")
      return "cx-ticket-state-badge cx-ticket-state-found-cause";
    if (value === "nextfollowup")
      return "cx-ticket-state-badge cx-ticket-state-next-followup";
    if (value === "closed")
      return "cx-ticket-state-badge cx-ticket-state-closed";

    return "cx-ticket-state-badge cx-ticket-state-default";
  };

  return (
    <div className="ticket-container">
      <TicketSidebar active="/customer/tickets" />

      <div className="ticket-main-content">
        {activeMenu === 1 && (
          <>
            <h2>Check Ticket Status</h2>
            <div className="ticket-search-bar">
              <input
                type="text"
                placeholder="Search Ticket Number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="ticket-table-scroll">
              <table className="ticket-table2">
                <thead>
                  <tr>
                    <th>Ticket</th>
                    <th>Issue Summary</th>
                    <th>Serial</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>View Ticket</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.length > 0 ? (
                    paginatedTickets.map((ticket) => (
                      <React.Fragment key={ticket.id}>
                        <tr>
                          <td>{ticket.id}</td>
                          <td>{ticket.issue_summary || "-"}</td>
                          <td>{ticket.customer_asset_serial || "-"}</td>
                          <td>
                            <span
                              className={getTicketStatusClass(ticket.status)}
                            >
                              {ticket.status || "-"}
                            </span>
                          </td>
                          <td>{ticket.created_at || "-"}</td>
                          <td style={{ textAlign: "center" }}>
                            <button
                              className="btn-primary"
                              onClick={() => handleRowClick(ticket)}
                            >
                              View
                            </button>
                          </td>
                        </tr>

                        {selectedTicket?.id === ticket.id && (
                          <tr className="ticket-detail-row">
                            <td colSpan="6">
                              <div className="ticket-chat">
                                <h3>
                                  Conversation for Ticket #{ticket.id}
                                  <button
                                    onClick={() => handleUrgentClick()}
                                    disabled={isDisabled}
                                    className={`urgent-button ${
                                      isDisabled ? "disabled" : ""
                                    }`}
                                    style={{
                                      backgroundColor: isDisabled
                                        ? "gray"
                                        : "red",
                                      cursor: isDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                      color: "white",
                                      padding: "10px 20px",
                                      border: "none",
                                      borderRadius: "5px",
                                    }}
                                  >
                                    {isDisabled
                                      ? "Please wait 1 hour."
                                      : "Urgent"}
                                  </button>
                                </h3>

                                <div className="chat-messages">
                                  {responses.map((response) => {
                                    const isCustomer =
                                      response.customer_id !== null &&
                                      response.customer_id !== undefined;

                                    const isAdmin =
                                      response.employee_id !== null &&
                                      response.employee_id !== undefined;
                                    if (!isCustomer && !isAdmin) return null;

                                    return (
                                      <div
                                        key={response.id}
                                        className={`chat-message ${
                                          isCustomer
                                            ? "cus-message"
                                            : isAdmin
                                              ? "admin-message2"
                                              : ""
                                        }`}
                                      >
                                        <div className="message-text">
                                          {response.Message}
                                        </div>

                                        {(() => {
                                          const files = normalizeAttach(
                                            response.AttachFiles,
                                          );
                                          if (files.length === 0) return null;
                                          return (
                                            <div className="attachment">
                                              <ul
                                                className="attachments-list"
                                                style={{
                                                  margin: 0,
                                                  paddingLeft: 18,
                                                }}
                                              >
                                                {files.map((raw, i) => {
                                                  const url = buildFileUrl(raw);
                                                  if (!url) return null;
                                                  return (
                                                    <li
                                                      key={i}
                                                      className="attachments-item"
                                                      style={{
                                                        listStyle: "disc",
                                                      }}
                                                    >
                                                      <a
                                                        className="attachments-link"
                                                        href={url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                      >
                                                        📄 {raw}
                                                      </a>
                                                    </li>
                                                  );
                                                })}
                                              </ul>
                                            </div>
                                          );
                                        })()}

                                        <div className="message-meta">
                                          <span>
                                            {isCustomer
                                              ? `From: ${response.senderName || "Customer"}`
                                              : `From: Admin (${response.senderName || "Unknown"})`}
                                          </span>
                                          <span>
                                            {formatDate(
                                              response?.created_at ?? "",
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                <div className="chat-input-container">
                                  <textarea
                                    className="chat-input"
                                    value={newMessage}
                                    onChange={(e) =>
                                      setNewMessage(e.target.value)
                                    }
                                    onKeyDown={handleKeyDown}
                                    placeholder="Type your message here..."
                                    rows="4"
                                    style={{ whiteSpace: "pre-wrap" }}
                                  />
                                  <div className="chat-actions">
                                    <input
                                      type="file"
                                      id="file-upload"
                                      onChange={handleFileChange}
                                      style={{ display: "none" }}
                                    />
                                    <label
                                      htmlFor="file-upload"
                                      className="file-upload-button"
                                    >
                                      {selectedFile
                                        ? "📎 " + selectedFile.name
                                        : "📎 Attach File"}
                                    </label>
                                    <button
                                      className="chat-send-button"
                                      onClick={handleSendMessage}
                                      disabled={
                                        isUploading ||
                                        (!newMessage.trim() && !selectedFile)
                                      }
                                    >
                                      {isUploading ? "Uploading..." : "Send"}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="ticket-no-data">
                        {notFoundMessage || "ไม่พบข้อมูล"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="ticket-pagination">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={currentPage === page ? "active-page" : ""}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Ticket;
